import { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';

interface NetworkInformation extends EventTarget {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

const COOLDOWN_MS = 60 * 1000; // 60 seconds between warnings
const TOAST_DURATION_MS = 8000;

function showSlowWarning(reason: string) {
  toast(
    `Slow connection detected${reason ? ` (${reason})` : ''}. Pages may take longer to load.`,
    {
      duration: TOAST_DURATION_MS,
      icon: '\u26a0\ufe0f',
      style: {
        background: '#fef3c7',
        color: '#92400e',
        border: '1px solid #f59e0b',
      },
    }
  );
}

export function useSlowConnection() {
  const lastWarningAt = useRef(0);

  useEffect(() => {
    const conn = navigator.connection;
    if (!conn) return;

    const checkConnection = () => {
      if (!conn.effectiveType) return;

      const isSlow =
        conn.effectiveType === '2g' ||
        conn.effectiveType === 'slow-2g' ||
        (conn.downlink !== undefined && conn.downlink < 0.5) ||
        (conn.rtt !== undefined && conn.rtt > 2000);

      if (isSlow && Date.now() - lastWarningAt.current > COOLDOWN_MS) {
        lastWarningAt.current = Date.now();
        const detail =
          conn.downlink !== undefined && conn.downlink < 0.5
            ? `${conn.downlink} Mbps`
            : conn.effectiveType;
        showSlowWarning(detail);
      }
    };

    checkConnection();
    conn.addEventListener('change', checkConnection);

    return () => {
      conn.removeEventListener('change', checkConnection);
    };
  }, []);
}
